import {
  buildWarehouseSettingsPayload,
  matchKeysHaveChanged,
  normalizeWarehouseSettings,
  parseMatchKeys,
} from "./warehouseSettings";
import {
  PageRecord,
  RecordAdapterContext,
  SaveConfirmation,
} from "../recordDefinition.types";

/**
 * The settings as the page edits them: match keys as rows, and the connection string
 * blank, since it is write-only - whether one is stored is kept beside it.
 */
export function adaptWarehouseSettings(
  read: PageRecord,
  { organisation }: RecordAdapterContext
): PageRecord {
  const settings = normalizeWarehouseSettings(read, organisation);

  return {
    ...settings,
    // The installation is the record, and the id its audits are kept under.
    id: settings.InstallationId,
    AzureConnectionString: "",
    hasAzureConnectionString: settings.AzureConnectionString.HasValue,
    MatchKeys: parseMatchKeys(settings.MatchKeys),
  };
}

export const toWarehouseSettingsPayload = (record: PageRecord) =>
  buildWarehouseSettingsPayload(record, record.MatchKeys);

export function warehouseSettingsSaveConfirmation(
  record: PageRecord,
  original: PageRecord | null
): SaveConfirmation {
  return {
    title: "Save Warehouse Settings",
    paragraphs: [
      "Secret values are write-only. Make a note of any replacement Azure connection string before saving.",
      ...(matchKeysHaveChanged(original?.MatchKeys ?? [], record.MatchKeys)
        ? [
            "Match key changes can affect applications that reference match key ids. Saving will regenerate ids from the displayed order.",
          ]
        : []),
    ],
  };
}

export type MatchKeyCellErrors = Partial<
  Record<"keyDefinition" | "score" | "creationScore", string>
>;

const isWholeNumber = (value: unknown) => Number.isInteger(Number(value));

/** What is wrong with each cell of each key, by row - the rules of validateMatchKeyRows. */
export function matchKeyCellErrors(rows: unknown): MatchKeyCellErrors[] {
  return (Array.isArray(rows) ? rows : []).map((row) => {
    const errors: MatchKeyCellErrors = {};
    const score = `${row.score ?? ""}`.trim();
    const creationScore = `${row.creationScore ?? ""}`.trim();

    if (!`${row.keyDefinition ?? ""}`.trim()) {
      errors.keyDefinition = "Enter a key definition";
    }
    if (score === "" || !isWholeNumber(score)) {
      errors.score = "Enter a whole-number score";
    }
    if (creationScore !== "" && !isWholeNumber(creationScore)) {
      errors.creationScore = "Enter a whole number, or leave blank";
    }

    return errors;
  });
}

export const warehouseSettingsValidators = {
  // Required only until one is stored, as it is never read back.
  warehouseAzureConnectionString: (value: unknown, record: PageRecord) =>
    !record.hasAzureConnectionString && !value
      ? "Azure Connection String is required"
      : "",
  // One line under the table; the cells themselves say what is wrong.
  warehouseMatchKeys: (value: unknown) =>
    matchKeyCellErrors(value).some((errors) => Object.keys(errors).length > 0)
      ? "Fix the highlighted match keys."
      : "",
};
